import ApiService from './ApiService';
import { API_BASE_URL } from '@config/config';
import LocalStorageService from '@services/LocalStorageService';
import LocalStorageKey from '@enums/LocalStorageKey';
import { MasqueradeUser } from '@customTypes/auth/Auth';

export interface CreateNewConversationRequest {
  message: string;
  conversation_id?: string;
  enable_streaming?: boolean;
  client_requirements_note?: string;
  is_test?: boolean;
  uploaded_images?: any[];
}

export interface NewConversationResponse200 {
  response: any;
  conversation_id: string;
  conversation_title?: string;
  streaming?: boolean;
  response_type?: string;
  confidence?: number;
  tool_calls_count?: number;
  message_count?: number;
  operation_id?: string;
  timestamp?: string;
}

export interface NewConversationResponse202 {
  response: {
    status: 'pending_approval';
    conversation_id: string;
    message: string;
    approval_data: any;
  };
}

export type NewConversationResponse =
  | NewConversationResponse200
  | NewConversationResponse202;

export interface NewConversationApiResult {
  data: NewConversationResponse;
  status: number | undefined;
}

export interface NewConversationListItem {
  conversation_id: string;
  title: string;
  timestamp: number;
  last_activity_at?: number;
  started_by_user_name?: string;
}

/**
 * SSE stream event with event ID for reconnection support.
 * The eventId corresponds to the SSE `id:` field and is used with
 * the `Last-Event-ID` header to resume streams after connection drops.
 */
export interface SSEStreamEvent<T = any> {
  /** SSE event ID (sequence number) for reconnection tracking */
  eventId: number;
  /** The parsed event data from the SSE `data:` field */
  data: T;
}

class NewConversationService {
  /**
   * Extract error message and code from a failed response
   */
  private async extractErrorFromResponse(
    response: Response,
    defaultMessage: string
  ): Promise<{ message: string; code?: string }> {
    try {
      const errorText = await response.text();
      if (!errorText) {
        return { message: defaultMessage };
      }

      try {
        const errorData = JSON.parse(errorText);
        return {
          message: errorData.error || errorData.message || defaultMessage,
          code: errorData.code,
        };
      } catch {
        // If not JSON, use the text as-is if it's meaningful
        return {
          message: errorText.trim().length > 0 && errorText.length < 500
            ? errorText.trim()
            : defaultMessage,
        };
      }
    } catch (parseError) {
      console.error('❌ [NewConversationService] Error reading error response:', parseError);
      return { message: defaultMessage };
    }
  }

  public async create(
    body: CreateNewConversationRequest,
  ): Promise<NewConversationApiResult> {
    const url = body.conversation_id
      ? `/new-conversations/${body.conversation_id}`
      : `/new-conversations`;
    
    // Initiating streaming request
    const absoluteUrl = `${API_BASE_URL}${url}`;
    
    // Always use streaming request handler since backend always streams
    const streamingResult = await this.createStreamingRequest(absoluteUrl, body);
    return streamingResult;
  }

  public async createStreaming(
    body: CreateNewConversationRequest,
    onChunk?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void
  ): Promise<NewConversationApiResult> {
    const url = body.conversation_id
      ? `/new-conversations/${body.conversation_id}`
      : `/new-conversations`;
    
    const absoluteUrl = `${API_BASE_URL}${url}`;
    
    return await this.createStreamingRequest(absoluteUrl, body, onChunk, onComplete, onError);
  }

  private async createStreamingRequest(
    url: string,
    body: CreateNewConversationRequest,
    onChunk?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void
  ): Promise<NewConversationApiResult> {
    console.log('🌐 [NewConversationService.createStreamingRequest] Starting fetch request', {
      url,
      body,
      timestamp: new Date().toISOString()
    });
    
    return new Promise((resolve, reject) => {
      let streamCompleted = false;
      let lastStreamData: any = null;

      // Resettable timeout — resets every time we receive ANY data from the
      // stream, including SSE keepalive comments (": keepalive\n\n").
      // This prevents the timeout from firing during long-running agent
      // operations where the backend is alive and sending keepalives every 30s.
      const STREAM_TIMEOUT_MS = 120000; // 120 seconds of NO activity
      let streamTimeoutId: ReturnType<typeof setTimeout>;

      const resetStreamTimeout = () => {
        clearTimeout(streamTimeoutId);
        if (streamCompleted) return;
        streamTimeoutId = setTimeout(() => {
          if (!streamCompleted) {
            console.warn('🕐 [NewConversationService] Stream timeout — no data received for 2 minutes');

            streamCompleted = true;

            // If we have any stream data, try to use it instead of failing completely
            if (lastStreamData && (lastStreamData.conversation_id || lastStreamData.response)) {
              console.log('⚠️ [NewConversationService] Using partial stream data due to timeout:', lastStreamData);
              resolve({
                data: {
                  conversation_id: lastStreamData.conversation_id || `timeout_${Date.now()}`,
                  response: lastStreamData.response || 'Request timed out but may have partial response. Please try again.',
                  streaming: true
                },
                status: 200
              });
            } else {
              console.error('❌ [NewConversationService] No stream data available after timeout');
              reject(new Error('Stream timeout - no response received within 2 minutes. This may be due to server overload or connection issues.'));
            }
          }
        }, STREAM_TIMEOUT_MS);
      };

      // Start the initial timeout
      resetStreamTimeout();

      // Create headers object, including masquerade header if present
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
      };

      // Add masquerade header if user is masquerading (same logic as ApiService)
      const masqueradeUser = LocalStorageService.getItem<MasqueradeUser>(
        LocalStorageKey.MASQUERADE_USER,
      );
      if (masqueradeUser?.id) {
        headers.Masquerade = masqueradeUser.id;
        console.log('🎭 [NewConversationService] Adding masquerade header for:', masqueradeUser.name, `(ID: ${masqueradeUser.id})`);
      }

      fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      })
      .then(async (response) => {
        console.log('🌐 [NewConversationService] Fetch response received', {
          status: response.status,
          statusText: response.statusText,
          contentType: response.headers.get('content-type'),
          isOk: response.ok
        });

        if (!response.ok) {
          clearTimeout(streamTimeoutId);
          console.error('❌ [API ERROR]', response.status, response.statusText);
          
          const { message, code } = await this.extractErrorFromResponse(
            response,
            `HTTP ${response.status}`
          );
          
          const error = new Error(message);
          if (code) {
            (error as any).code = code;
          }
          
          if (onError) {
            onError(message);
          }
          throw error;
        }

        if (response.headers.get('content-type')?.includes('text/event-stream')) {
          console.log('🔄 [NewConversationService] Streaming response detected, creating EventSource');
          // Streaming response detected — pass resetStreamTimeout so the
          // timeout resets on every chunk (including keepalives)
          const eventSource = this.createEventSourceFromResponse(response, resetStreamTimeout);
          
          let finalResult: any = null;
          
          eventSource.addEventListener('message', (event) => {
            console.log('📨 [NewConversationService] EventSource message received', {
              eventData: event.data?.slice(0, 100) + (event.data?.length > 100 ? '...' : ''),
              eventLength: event.data?.length || 0,
              timestamp: new Date().toISOString()
            });
            
            try {
              const data = JSON.parse(event.data);
              lastStreamData = data;
              
              console.log('📊 [NewConversationService] Parsed event data', {
                type: data.type,
                hasData: !!data.data,
                dataKeys: data.data ? Object.keys(data.data) : [],
                timestamp: new Date().toISOString()
              });
              
              if (data.type === 'complete') {
                console.log('✅ [COMPLETE] Streaming finished, resolving promise', {
                  hasData: !!data.data,
                  conversationId: data.data?.conversation_id,
                  responseLength: data.data?.response?.length || 0,
                  rawDataKeys: data.data ? Object.keys(data.data) : [],
                  rawCompleteData: JSON.stringify(data).slice(0, 200) + '...'
                });
                streamCompleted = true;
                clearTimeout(streamTimeoutId);
                finalResult = data;
                
                // Call onComplete callback if provided
                if (onComplete) {
                  onComplete(data);
                }
                
                console.log('🔍 [COMPLETE] About to close EventSource and resolve', {
                  hasDataObject: !!finalResult.data,
                  dataType: typeof finalResult.data,
                  willResolve: !!finalResult.data
                });
                
                eventSource.close();
                
                // Ensure we have valid data before resolving
                if (finalResult.data) {
                  resolve({
                    data: finalResult.data,
                    status: response.status
                  });
                } else {
                  console.error('❌ [COMPLETE] No data in complete event');
                  reject(new Error('Stream completed but no data received'));
                }
              } else if (data.type === 'error') {
                console.error('❌ [ERROR]', data.message);
                streamCompleted = true;
                clearTimeout(streamTimeoutId);
                eventSource.close();
                
                // Call onError callback if provided
                if (onError) {
                  onError(data.message || 'Streaming error');
                }
                
                reject(new Error(data.message || 'Streaming error'));
              } else {
                // Handle other event types (chunk, tools_loading, etc.) via callbacks
                // Call onChunk for all non-complete, non-error events
                if (onChunk) {
                  onChunk(data);
                }
              }
              
              // Always call handleStreamingEvent for CustomEvent dispatch (backward compatibility)
              console.log('🔀 [NewConversationService] Calling handleStreamingEvent with data type:', data.type);
              this.handleStreamingEvent(data);
              
            } catch (e) {
              console.error('❌ [NewConversationService] Error parsing event data:', e);
              streamCompleted = true;
              clearTimeout(streamTimeoutId);
              eventSource.close();
              
              const errorMessage = 'Invalid streaming data format';
              if (onError) {
                onError(errorMessage);
              }
              
              reject(new Error(errorMessage));
            }
          });

          eventSource.addEventListener('error', (errorEvent) => {
            console.error('❌ [NewConversationService] EventSource error:', errorEvent);
            console.log('🔍 [ERROR DEBUG] Error details:', {
              type: errorEvent.type,
              eventPhase: errorEvent.eventPhase,
              target: errorEvent.target,
              streamCompleted,
              lastStreamDataType: lastStreamData?.type,
              timestamp: new Date().toISOString()
            });
            streamCompleted = true;
            clearTimeout(streamTimeoutId);
            eventSource.close();
            
            const errorMessage = 'Streaming connection failed';
            if (onError) {
              onError(errorMessage);
            }
            
            reject(new Error(errorMessage));
          });
          
        } else {
          clearTimeout(streamTimeoutId);
          console.log('📄 [NewConversationService] Regular JSON response detected');
          // Regular JSON response
          const data = await response.json();
          console.log('📄 [NewConversationService] JSON response data:', data);
          resolve({ data, status: response.status });
        }
      })
      .catch((error) => {
        clearTimeout(streamTimeoutId);
        console.error('❌ [NewConversationService] Fetch error:', error);
        
        // Provide more user-friendly error messages for common network issues
        let userFriendlyMessage = error.message;
        if (error.message?.includes('ERR_HTTP2_PROTOCOL_ERROR')) {
          userFriendlyMessage = 'Connection issue with server (HTTP/2 protocol error). Please try again.';
        } else if (error.message?.includes('fetch')) {
          userFriendlyMessage = 'Network connection failed. Please check your internet connection and try again.';
        } else if (error.message?.includes('timeout')) {
          userFriendlyMessage = 'Request timed out. The server may be busy, please try again in a few moments.';
        }
        
        if (onError) {
          onError(userFriendlyMessage);
        }
        
        reject(new Error(userFriendlyMessage));
      });
    });
  }


  private createEventSourceFromResponse(response: Response, onActivity?: () => void): EventSource {
    // Note: This is a simplified approach. In practice, you might need
    // to handle the stream manually since EventSource has limitations
    // with POST requests and custom headers
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    // Create a mock EventSource-like object
    const mockEventSource = {
      addEventListener: (type: string, listener: (event: any) => void) => {
        if (type === 'message' && reader) {
          this.processStream(reader, decoder, listener, onActivity);
        }
      },
      close: () => {
        reader?.cancel();
      }
    };

    return mockEventSource as any;
  }

  private async processStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    decoder: TextDecoder,
    messageListener: (event: any) => void,
    onActivity?: () => void
  ) {
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Signal activity on ANY data received — including SSE keepalive
        // comments (": keepalive\n\n"). This resets the stream timeout so
        // long-running agent operations don't get killed prematurely.
        onActivity?.();

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete lines, keeping incomplete ones in buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep the last (potentially incomplete) line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data && data !== '[]') { // Filter out empty array separators
              try {
                // Validate that it's proper JSON before processing
                const parsedData = JSON.parse(data);
                
                // Enhanced filtering: Skip tool call debug data even at this level
                if (parsedData.type === 'chunk' && parsedData.data) {
                  const chunkContent = parsedData.data.trim();
                  // Filter out tool call metadata that shouldn't be shown to users
                  if (chunkContent.startsWith('[{') && 
                      (chunkContent.includes('toolu_') || 
                       chunkContent.includes('partial_json') || 
                       chunkContent.includes('input_json_delta'))) {
                    console.log('🚫 [NewConversationService] Filtering tool debug chunk:', chunkContent.slice(0, 100) + '...');
                    continue; // Skip this chunk
                  }
                }
                
                messageListener({ data: data } as MessageEvent);
              } catch (parseError) {
                console.warn('Skipping malformed JSON chunk:', data.slice(0, 100));
              }
            }
          }
        }
      }
      
      // Process any remaining data in buffer
      if (buffer.trim()) {
        const line = buffer.trim();
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data && data !== '[]') {
            try {
              const parsedData = JSON.parse(data);
              
              // Enhanced filtering: Skip tool call debug data even at this level  
              if (parsedData.type === 'chunk' && parsedData.data) {
                const chunkContent = parsedData.data.trim();
                // Filter out tool call metadata that shouldn't be shown to users
                if (chunkContent.startsWith('[{') && 
                    (chunkContent.includes('toolu_') || 
                     chunkContent.includes('partial_json') || 
                     chunkContent.includes('input_json_delta'))) {
                  console.log('🚫 [NewConversationService] Filtering tool debug chunk in buffer:', chunkContent.slice(0, 100) + '...');
                  return; // Skip this chunk
                }
              }
              
              messageListener({ data: data } as MessageEvent);
            } catch (parseError) {
              console.warn('Skipping malformed JSON in final buffer:', data.slice(0, 100));
            }
          }
        }
      }
    } catch (error) {
      console.error('Stream processing error:', error);
    }
  }

  private handleStreamingEvent(data: any) {
    console.log('🎪 [NewConversationService.handleStreamingEvent] Creating CustomEvent', {
      type: data.type,
      hasData: !!data.data,
      dataPreview: data.data?.slice?.(0, 50) + (data.data?.length > 50 ? '...' : ''),
      timestamp: new Date().toISOString()
    });
    
    // Emit custom events or call callbacks for different streaming events
    // This allows the UI to respond to streaming progress
    const event = new CustomEvent('conversationStreaming', {
      detail: data
    });
    
    console.log('🎪 [NewConversationService.handleStreamingEvent] Dispatching event to window');
    window.dispatchEvent(event);
    console.log('🎪 [NewConversationService.handleStreamingEvent] Event dispatched successfully');
  }

  public async approve(conversationId: string, body: any): Promise<any> {
    const { success, data, error } = await ApiService.post<any, any>(
      `/new-conversations/${conversationId}/approval`,
      body,
    );
    if (success) return data;
    throw new Error(String(error) || 'NewConversationService::approve failed');
  }

  /**
   * Submit questionnaire answers and stream the agent's continuation response.
   * The backend resumes the interrupted LangGraph and streams SSE events.
   */
  public async *streamQuestionnaire(
    conversationId: string,
    answers: Array<{ question_id: string; selected_option: string | null; freeform_text: string | null }>,
    signal?: AbortSignal,
  ): AsyncGenerator<SSEStreamEvent, void, unknown> {
    const { API_BASE_URL } = await import('@config/config');
    const url = `${API_BASE_URL}/new-conversations/${conversationId}/questionnaire`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const masqueradeUser = LocalStorageService.getItem<MasqueradeUser>(
      LocalStorageKey.MASQUERADE_USER,
    );
    if (masqueradeUser?.id) {
      headers.Masquerade = masqueradeUser.id;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ answers }),
      signal,
    });

    if (!response.ok) {
      const { message, code } = await this.extractErrorFromResponse(
        response,
        `Questionnaire stream failed: ${response.statusText}`
      );
      const error = new Error(message);
      if (code) {
        (error as any).code = code;
      }
      throw error;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventId = 0;

    if (!reader) {
      throw new Error('No reader available');
    }

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('id: ')) {
          const parsedId = parseInt(line.slice(4), 10);
          if (!isNaN(parsedId)) {
            currentEventId = parsedId;
          }
        } else if (line.startsWith(':')) {
          yield { eventId: currentEventId, data: { type: 'keepalive' } };
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            yield { eventId: currentEventId, data };
          } catch (e) {
            console.warn('Failed to parse SSE data:', line, e);
          }
        }
      }
    }
  }

  public async getAll(): Promise<NewConversationListItem[]> {
    const { success, data, error } = await ApiService.get<
      NewConversationListItem[]
    >('/new-conversations');
    if (success) return (data as unknown) as NewConversationListItem[];
    throw new Error(String(error) || 'NewConversationService::getAll failed');
  }

  public async getById(conversationId: string): Promise<any> {
    const { success, data, error } = await ApiService.get<any>(
      `/new-conversations/${conversationId}`,
    );
    if (success) return data as any;
    throw new Error(String(error) || 'NewConversationService::getById failed');
  }

  public async delete(conversationId: string): Promise<void> {
    const { success, error } = await ApiService.delete(
      `/new-conversations/${conversationId}`,
    );
    if (!success) {
      throw new Error(String(error) || 'NewConversationService::delete failed');
    }
  }

  /**
   * Signal cooperative cancel for a streaming conversation (worker mode).
   * @returns true if cancel was accepted (worker env), false if endpoint returned error (e.g. workers not enabled).
   */
  public async cancelStream(conversationId: string): Promise<boolean> {
    try {
      const { success, error } = await ApiService.post<undefined, { cancel_requested?: boolean }>(
        `/new-conversations/${conversationId}/cancel`,
        undefined,
      );
      if (!success) {
        console.warn('[NewConversationService.cancelStream] Cancel request failed:', error);
        return false;
      }
      return true;
    } catch (e) {
      console.warn('[NewConversationService.cancelStream] Error calling cancel endpoint:', e);
      return false;
    }
  }

  public async submitFeedback(conversationId: string, value: 0 | 1, messageId?: string, comment?: string): Promise<string | null> {
    try {
      const { success, data } = await ApiService.post<
        { value: number; message_id?: string; comment?: string },
        { ok: boolean; score_id: string }
      >(`/new-conversations/${conversationId}/feedback`, {
        value,
        message_id: messageId,
        comment,
      });
      if (success && data?.score_id) {
        return data.score_id;
      }
      return null;
    } catch (e) {
      console.warn('[NewConversationService.submitFeedback] Error:', e);
      return null;
    }
  }

  public async updateFeedbackComment(conversationId: string, scoreId: string, comment: string): Promise<void> {
    try {
      await ApiService.patch(`/new-conversations/${conversationId}/feedback/${scoreId}`, {
        comment,
      });
    } catch (e) {
      console.warn('[NewConversationService.updateFeedbackComment] Error:', e);
    }
  }

  public stream(conversationId: string, operationId: string): EventSource {
    // NOTE: This method is deprecated. Streaming is now handled directly 
    // in the create() method when enable_streaming is true.
    // This method is kept for backward compatibility but will not work
    // since the /stream endpoint has been removed.
    console.warn('NewConversationService.stream() is deprecated. Use create() with enable_streaming: true instead.');
    
    const path = `/new-conversations/${conversationId}/stream?operation_id=${encodeURIComponent(
      operationId,
    )}`;
    const absolute = `${API_BASE_URL}${path}`;
    return new EventSource(absolute);
  }

  /**
   * Stream message using SSE (POST method)
   * Returns async generator that yields chunks
   */
  /**
   * Stream message with SSE reconnection support.
   * 
   * @param conversationId - The conversation ID to stream
   * @param message - The message to send
   * @param uploadedImages - Optional uploaded images
   * @param isRetry - Whether this is a retry request
   * @param lastEventId - Last event ID received (for reconnection via Last-Event-ID header)
   * @param signal - Optional AbortSignal to cancel the stream
   * @yields SSE events with their event IDs for tracking reconnection state
   */
  public async *streamMessage(
    conversationId: string,
    message: string,
    uploadedImages?: any[],
    isRetry?: boolean,
    lastEventId?: number,
    isHidden?: boolean,
    signal?: AbortSignal,
    displayText?: string
  ): AsyncGenerator<SSEStreamEvent, void, unknown> {
    const { API_BASE_URL } = await import('@config/config');
    const url = `${API_BASE_URL}/new-conversations/${conversationId}/stream`;
    
    // Prepare request body
    const requestBody: any = { message };
    if (uploadedImages && uploadedImages.length > 0) {
      requestBody.uploaded_images = uploadedImages;
    }
    if (isRetry) {
      requestBody.is_retry = true;
    }
    if (isHidden) {
      requestBody.is_hidden = true;
    }
    if (displayText) {
      requestBody.display_text = displayText;
    }
    
    // Create headers object, including masquerade header if present
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add Last-Event-ID header for SSE reconnection
    // This tells the backend to skip events we've already received
    if (lastEventId !== undefined && lastEventId > 0) {
      headers['Last-Event-ID'] = String(lastEventId);
      console.log(`🔄 [NewConversationService.streamMessage] Reconnecting from event ${lastEventId}`);
    }

    // Add masquerade header if user is masquerading (same logic as createStreamingRequest)
    const masqueradeUser = LocalStorageService.getItem<MasqueradeUser>(
      LocalStorageKey.MASQUERADE_USER,
    );
    if (masqueradeUser?.id) {
      headers.Masquerade = masqueradeUser.id;
      console.log('🎭 [NewConversationService.streamMessage] Adding masquerade header for:', masqueradeUser.name, `(ID: ${masqueradeUser.id})`);
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!response.ok) {
      const { message, code } = await this.extractErrorFromResponse(
        response,
        `Stream failed: ${response.statusText}`
      );
      
      const error = new Error(message);
      if (code) {
        (error as any).code = code;
      }
      
      throw error;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventId = 0;

    if (!reader) {
      throw new Error('No reader available');
    }

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

        for (const line of lines) {
        // Parse SSE event ID line (format: "id: <number>")
        if (line.startsWith('id: ')) {
          const parsedId = parseInt(line.slice(4), 10);
          if (!isNaN(parsedId)) {
            currentEventId = parsedId;
          }
        }
          // Handle SSE keepalive comments (format: ": <comment>")
          else if (line.startsWith(':')) {
            yield { eventId: currentEventId, data: { type: 'keepalive' } };
          }
        // Parse SSE data line (format: "data: <json>")
        else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            // Yield both the event ID and data for reconnection tracking
            yield { eventId: currentEventId, data };
          } catch (e) {
            console.warn('Failed to parse SSE data:', line, e);
          }
        }
        // Ignore SSE comments (format: ": <comment>") - these are keepalives
      }
    }
  }
}

export default new NewConversationService();


